/** Detect seller platform issues and build support context for the AI agent. */

const STORE_LOGO_PATTERNS = [
  /\bstore\s*logo\b/i,
  /\bshop\s*logo\b/i,
  /\bbrand\s*logo\b/i,
  /\blogo\b/i,
  /\bprofile\s*(pic|photo|picture|image)\b/i,
  /\bstore\s*(photo|picture|image|avatar)\b/i,
  /\baccount\s*settings\b/i,
];

const STORE_BANNER_PATTERNS = [
  /\bbanner\b/i,
  /\bcover\s*(image|photo|picture)\b/i,
  /\bstore\s*cover\b/i,
];

const PRODUCT_IMAGE_PATTERNS = [
  /\bproduct\s*(image|photo|picture|pic|thumbnail)\b/i,
  /\bproduct\s*catalog\b/i,
  /\blisting\s*(image|photo)\b/i,
  /\bitem\s*(image|photo)\b/i,
  /\bthumbnail\b/i,
  /\bsku\b/i,
  /\bcatalog\b/i,
];

const GENERIC_IMAGE_PATTERNS = [
  /image(s)?\s*(not|isn't|isnt|won't|wont|doesn't|doesnt|aren't|arent)\s*(show|display|load|appear|visible)/i,
  /(photo|picture)\s*(not|nahi|nhi|no)\s*(show|display|load|appear|visible|dikh)/i,
  /(show|display|load|appear|visible|dikh)\s*(nahi|nhi|not).*(image|photo|picture)/i,
  /broken\s*(image|photo|picture)/i,
  /blank\s*(image|photo|thumbnail)/i,
];

const ISSUE_DEFINITIONS = [
  {
    id: 'store_logo_issue',
    label: 'Store logo not showing',
    patterns: [
      ...STORE_LOGO_PATTERNS.map((p) => new RegExp(`${p.source}.*(not|nahi|nhi|show|display|load|issue|problem|wrong|update|change|upload)`, 'i')),
      /(not|nahi|nhi|show|display|load|issue|problem|wrong).*(store\s*logo|shop\s*logo|brand\s*logo|\blogo\b)/i,
    ],
    imageIntent: 'store_logo',
    tools: ['get_seller_approval_status', 'get_my_stores'],
    steps: [
      'This is a STORE LOGO issue — not a product image. Store logo appears in header/store profile; product images are per product in catalog.',
      'If unclear whether they mean logo vs product photo, ask ONE question before any action: "Is this about your store logo or a product image?"',
      'Store logo fix: Account Settings → upload logo, OR attach logo in chat and confirm it is the store logo → then apply_store_image with imageType profile.',
      'Logo shows on storefront header once store is approved; it is separate from product thumbnails.',
      'Do NOT use create_product or product catalog steps for store logo issues.',
    ],
    pagePaths: ['/dashboard/user'],
  },
  {
    id: 'store_banner_issue',
    label: 'Store banner/cover not showing',
    patterns: [
      ...STORE_BANNER_PATTERNS.map((p) => new RegExp(`${p.source}.*(not|issue|problem|upload|change)`, 'i')),
      /(banner|cover).*(not|nahi|nhi|show|display|load|issue|problem)/i,
    ],
    imageIntent: 'store_banner',
    tools: ['get_my_stores'],
    steps: [
      'This is a STORE BANNER/COVER issue — different from store logo and product images.',
      'Fix: Account Settings or attach cover image in chat and confirm → apply_store_image with imageType cover.',
      'Do NOT treat banner as product thumbnail.',
    ],
    pagePaths: ['/dashboard/user'],
  },
  {
    id: 'product_image_issue',
    label: 'Product image not showing',
    patterns: [
      ...PRODUCT_IMAGE_PATTERNS.map((p) => new RegExp(`${p.source}.*(not|nahi|nhi|show|display|load|issue|problem|missing|blank)`, 'i')),
      /(not|nahi|nhi|show|display|load|issue|problem|missing|blank).*(product\s*(image|photo|picture|thumbnail)|thumbnail|catalog)/i,
    ],
    imageIntent: 'product_image',
    tools: ['get_seller_approval_status', 'check_subscription_status', 'get_my_stores'],
    steps: [
      'This is a PRODUCT IMAGE issue — not the store logo. Product images belong to individual products in Product Catalog.',
      'If product name is unknown, ask which product before giving steps.',
      'Fix: Product Catalog → edit product → re-upload JPG/PNG/WebP → Save.',
      'If uploaded via chat for a new product: confirm product name and price, then use image URL as thumbnail_url in create_product flow.',
      'Product visible on website needs approved store + active subscription — check silently.',
      'Do NOT call apply_store_image for product photos.',
    ],
    pagePaths: ['/dashboard/product'],
  },
  {
    id: 'image_issue_unclear',
    label: 'Image issue — type unclear',
    patterns: GENERIC_IMAGE_PATTERNS,
    imageIntent: 'unclear',
    requiresClarification: true,
    tools: ['get_seller_approval_status', 'check_subscription_status'],
    steps: [
      'DO NOT ASSUME whether this is store logo or product image.',
      'Ask exactly ONE clarifying question first: "To help you correctly — is this about your store logo, or a product image in your catalog?"',
      'Wait for seller answer before giving fix steps or calling apply_store_image / create_product.',
      'After they confirm: store logo → Account Settings / apply_store_image; product image → Product Catalog edit / create_product flow.',
    ],
    pagePaths: ['/dashboard/product', '/dashboard/user'],
  },
  {
    id: 'product_not_visible',
    label: 'Product not visible on store/website',
    patterns: [
      /product(s)?\s*(not|nahi|nhi)\s*(show|visible|live|appear|display)/i,
      /(store|website|shop)\s*(not|nahi|nhi).*(show|display).*(product)/i,
      /customer(s)?\s*(can't|cannot|cant)\s*see\s*(product|store)/i,
      /product(s)?\s*not\s*live/i,
    ],
    imageIntent: null,
    tools: ['get_seller_approval_status', 'check_subscription_status', 'get_my_stores'],
    steps: [
      'Ask which product if not specified — do not assume which listing.',
      'Silently check store approval and subscription status first.',
      'If approval is pending: explain products go live after admin approves the store — this is normal.',
      'If approved but no subscription: guide seller to Subscription page to activate a plan.',
      'If both OK: check product exists in Product Catalog and is not draft; suggest editing and saving again.',
      'Mention products need at least name, price, category, and image.',
    ],
    pagePaths: ['/dashboard/product', '/dashboard/Subscriptions'],
  },
  {
    id: 'subscription_issue',
    label: 'Subscription or payment issue',
    patterns: [
      /subscription\s*(issue|problem|error|expired|inactive|not working)/i,
      /payment\s*(fail|failed|error|issue|problem|not working|declined)/i,
      /(pay|paid|paytabs).*(not|fail|error|issue)/i,
      /(plan|package)\s*(not|expire|inactive)/i,
      /can't\s*add\s*product/i,
      /cannot\s*add\s*product/i,
    ],
    imageIntent: null,
    tools: ['get_seller_approval_status', 'check_subscription_status'],
    steps: [
      'Silently check approval — subscription payment only after store is approved.',
      'Silently check subscription status and show plan name/expiry if active.',
      'If inactive: call get_subscription_packages and guide to choose a plan; payment opens PayTabs.',
      'If payment failed: ask seller to complete store address in Account Settings, retry payment, try another card.',
      'If expired: explain renewal on Subscription page.',
    ],
    pagePaths: ['/dashboard/Subscriptions', '/dashboard/user'],
  },
  {
    id: 'approval_pending',
    label: 'Store approval waiting or rejected',
    patterns: [
      /approval\s*(pending|wait|waiting|issue|status)/i,
      /(store|account)\s*(not|still)\s*approved/i,
      /admin\s*(review|approval|approve)/i,
      /(pending|waiting)\s*(approval|review)/i,
      /rejected|suspended/i,
    ],
    imageIntent: null,
    tools: ['get_seller_approval_status'],
    steps: [
      'Silently call get_seller_approval_status and report exact status in plain language.',
      'Pending: reassure review is normal (24-48h typical), no payment needed yet, can check status anytime.',
      'Rejected/suspended: direct to support@airstudiosa.com with store name.',
      'Do NOT offer subscription or products until approved.',
    ],
    pagePaths: ['/dashboard'],
  },
  {
    id: 'login_access',
    label: 'Login or access problem',
    patterns: [
      /(login|log in|sign in)\s*(issue|problem|error|fail|not working)/i,
      /can't\s*(login|log in|access|sign in)/i,
      /otp\s*(not|fail|issue|problem)/i,
      /password\s*(reset|forgot|issue|problem)/i,
      /(token|session)\s*(expired|invalid)/i,
    ],
    imageIntent: null,
    tools: [],
    steps: [
      'Suggest logout, clear browser cache, login again with registered email.',
      'For OTP: check spam folder, wait 60s, request new code, verify phone format +966.',
      'For password: use Forgot Password on login page.',
      'If still stuck: support@airstudiosa.com with registered email.',
    ],
    pagePaths: ['/auth/login', '/dashboard/user'],
  },
  {
    id: 'csv_import',
    label: 'CSV/Excel import problem',
    patterns: [
      /(csv|excel|xlsx|import|bulk)\s*(issue|problem|error|fail|not working)/i,
      /import\s*(fail|failed|error)/i,
      /upload\s*(csv|excel|file).*(error|fail|problem)/i,
    ],
    imageIntent: null,
    tools: ['check_subscription_status', 'preview_bulk_import'],
    steps: [
      'Check subscription is active before bulk import.',
      'Ask seller to attach CSV/XLSX in chat or confirm file has name and price columns.',
      'Call preview_bulk_import to show validation errors by row.',
      'Offer get_bulk_import_template if format is wrong.',
      'Only import after seller confirms preview.',
    ],
    pagePaths: ['/dashboard/product'],
  },
  {
    id: 'printful_issue',
    label: 'Printful connection or sync issue',
    patterns: [
      /printful\s*(issue|problem|error|fail|not working|sync)/i,
      /(sync|connect).*(printful)/i,
      /print\s*on\s*demand\s*(issue|problem)/i,
    ],
    imageIntent: null,
    tools: ['check_printful_connection', 'check_subscription_status'],
    steps: [
      'Silently check Printful connection status.',
      'If disconnected: guide OAuth connect from Printful dashboard section.',
      'If connected but products missing: check subscription, then sync from Printful page.',
      'Explain Printful products need active subscription to show on storefront.',
    ],
    pagePaths: ['/dashboard/printful'],
  },
  {
    id: 'order_issue',
    label: 'Order or fulfillment issue',
    patterns: [
      /order(s)?\s*(issue|problem|error|stuck|pending|not)/i,
      /(fulfill|shipment|shipping)\s*(issue|problem|error)/i,
      /customer\s*order\s*(not|issue|problem)/i,
    ],
    imageIntent: null,
    tools: ['get_my_stores'],
    steps: [
      'Ask for order ID if not provided — do not assume which order.',
      'Explain order statuses: pending → processing → shipped → delivered.',
      'Guide seller to Orders page to update status and reply in Customer Chats.',
      'For Printful orders: mention auto-fulfillment after Printful sync.',
    ],
    pagePaths: ['/dashboard/order', '/dashboard/chat'],
  },
];

const GENERAL_SUPPORT_PATTERNS = [
  /\b(issue|problem|error|wrong|broken|bug|glitch|stuck|help me|fix this|not working|doesn't work|dont work|kuch galat|masla|pareshani)\b/i,
  /\b(something\s+is\s+wrong|why\s+(is|isn't|isnt|cant|can't))\b/i,
];

const PAGE_SUPPORT_HINTS = {
  '/dashboard/product': 'Seller is on Product Catalog — if image issue, clarify product image vs store logo before fixing.',
  '/dashboard/product/create': 'Seller is creating a product — attached images are usually product photos; confirm if unclear.',
  '/dashboard/Subscriptions': 'Seller is on Subscription — common issues: payment failed, plan inactive, cannot add products.',
  '/dashboard/sale': 'Seller is on Sales — help with discount setup and why sale may not apply.',
  '/dashboard/order': 'Seller is on Orders — help with order status and fulfillment.',
  '/dashboard/chat': 'Seller is on Customer Chats — help with messaging and notifications.',
  '/dashboard/printful': 'Seller is on Printful — help connect account and sync products.',
  '/dashboard/user': 'Seller is on Account Settings — attached images may be store logo/banner; confirm before apply_store_image.',
  '/dashboard/blog': 'Seller is on Blog — help create and publish posts.',
};

const CLARIFY_LOGO_VS_PRODUCT =
  'To help you correctly — is this about your **store logo** (shop branding in header/profile) or a **product image** (photo on a specific product in your catalog)?';

export const inferImageAttachmentIntent = (text, attachments = []) => {
  const t = String(text || '');
  const hasImageAttachment = attachments.some((a) => a.type === 'image');

  const mentionsLogo = STORE_LOGO_PATTERNS.some((p) => p.test(t));
  const mentionsBanner = STORE_BANNER_PATTERNS.some((p) => p.test(t));
  const mentionsProduct = PRODUCT_IMAGE_PATTERNS.some((p) => p.test(t));

  if (mentionsBanner && !mentionsProduct) return { intent: 'store_banner', needsClarification: false };
  if (mentionsLogo && !mentionsProduct && !mentionsBanner) return { intent: 'store_logo', needsClarification: false };
  if (mentionsProduct && !mentionsLogo && !mentionsBanner) return { intent: 'product_image', needsClarification: false };

  if ((mentionsLogo || mentionsProduct || mentionsBanner) && (mentionsLogo !== mentionsProduct || mentionsBanner)) {
    if (mentionsLogo && mentionsProduct) {
      return { intent: 'unclear', needsClarification: true, clarifyQuestion: CLARIFY_LOGO_VS_PRODUCT };
    }
  }

  if (GENERIC_IMAGE_PATTERNS.some((p) => p.test(t)) && !mentionsLogo && !mentionsProduct && !mentionsBanner) {
    return { intent: 'unclear', needsClarification: true, clarifyQuestion: CLARIFY_LOGO_VS_PRODUCT };
  }

  if (hasImageAttachment && !t.trim()) {
    return { intent: 'unclear', needsClarification: true, clarifyQuestion: CLARIFY_LOGO_VS_PRODUCT };
  }

  if (hasImageAttachment && t.trim() && !mentionsLogo && !mentionsProduct && !mentionsBanner) {
    return { intent: 'unclear', needsClarification: true, clarifyQuestion: CLARIFY_LOGO_VS_PRODUCT };
  }

  return { intent: null, needsClarification: false };
};

export const isSupportRequest = (text) => {
  const t = String(text || '');
  if (ISSUE_DEFINITIONS.some((issue) => issue.patterns.some((p) => p.test(t)))) return true;
  return GENERAL_SUPPORT_PATTERNS.some((p) => p.test(t));
};

export const detectSellerIssue = (text, pathname = '') => {
  const t = String(text || '');

  const issue = ISSUE_DEFINITIONS.find((issueDef) =>
    issueDef.patterns.some((pattern) => pattern.test(t))
  );
  if (issue) return issue;

  if (GENERAL_SUPPORT_PATTERNS.some((p) => p.test(t))) {
    const pageEntry = Object.entries(PAGE_SUPPORT_HINTS).find(([path]) =>
      String(pathname || '').includes(path)
    );
    return {
      id: 'general_support',
      label: 'General platform issue',
      patterns: [],
      imageIntent: null,
      requiresClarification: true,
      tools: ['get_seller_approval_status', 'check_subscription_status'],
      steps: [
        'Listen carefully — do NOT assume what the seller means.',
        'Ask ONE clarifying question if anything is ambiguous (which product, logo vs product image, which page).',
        'Silently check approval and subscription status to rule out common blockers.',
        'Give 2-4 numbered practical steps only after you understand the issue.',
        'If you cannot fix in chat: support@airstudiosa.com with store name + screenshot + steps tried.',
      ],
      pagePaths: pageEntry ? [pageEntry[0]] : [],
      pageHint: pageEntry?.[1] || 'Seller needs general platform help.',
    };
  }

  return null;
};

export const getPageSupportHint = (pathname) => {
  const entry = Object.entries(PAGE_SUPPORT_HINTS).find(([path]) =>
    String(pathname || '').includes(path)
  );
  return entry?.[1] || null;
};

export const buildSellerSupportContext = (text, pathname, attachments = []) => {
  const issue = detectSellerIssue(text, pathname);
  const pageHint = getPageSupportHint(pathname);
  const imageContext = inferImageAttachmentIntent(text, attachments);

  if (!issue && !pageHint && !imageContext.needsClarification) return null;

  const requiresClarification =
    Boolean(issue?.requiresClarification) || imageContext.needsClarification;

  return {
    isSupportRequest: Boolean(issue) || isSupportRequest(text) || imageContext.needsClarification,
    issueType: issue?.id || (imageContext.needsClarification ? 'image_issue_unclear' : null),
    issueLabel: issue?.label || (imageContext.needsClarification ? 'Image issue — type unclear' : null),
    imageIntent: issue?.imageIntent || imageContext.intent || null,
    requiresClarification,
    clarifyQuestion: imageContext.clarifyQuestion || null,
    recommendedTools: issue?.tools || ['get_seller_approval_status', 'check_subscription_status'],
    playbookSteps: issue?.steps || [],
    pageHint,
    currentPath: pathname,
    hasImageAttachment: attachments.some((a) => a.type === 'image'),
  };
};
